import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { Send, Truck } from "lucide-react";
import Layout from "@/components/Layout";
import FormLogo from "@/components/FormLogo";
import EmergencyToggle from "@/components/EmergencyToggle";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import {
  Section, Field, TopNotice,
  ApplicantSpocSection, RequestedBySection, SelfSkipSection,
  isHodDropdownOptional, stripSelfSkipTransients, ResubmitFromSection,
} from "@/components/FormShell";

/**
 * Phase 12 — Internal Maini Booking (form_type = "maini").
 * 3-stage workflow:
 *   submitted → cao_review → process_owner_review (Shivmohan.V) → completed.
 */
export default function NewMaini() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [depts, setDepts] = useState([]);
  const [hods, setHods] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [isResubmit, setIsResubmit] = useState(false);
  const [parentRefCode, setParentRefCode] = useState(null);

  const [form, setForm] = useState({
    staffName: user?.name || "",
    staffEmail: user?.email || "",
    staffContact: "",
    mainDept: "", subDept: "",
    hodApproval: "",
    is_hod_self: undefined, is_admin1_self: undefined,
    admin1_self_cost: "", admin1_self_remark: "",
    utility_purpose: "",
    required_date: "",
    required_time: "",
    is_emergency: false,
    emergency_reason: "",
  });

  useEffect(() => {
    api.get("/departments").then((r) => setDepts(r.data)).catch(() => {});
    api.get("/hods").then((r) => setHods(r.data)).catch(() => {});
  }, []);

  const subDepts = useMemo(() => {
    const d = depts.find((x) => x.name === form.mainDept);
    return d?.sub_departments || [];
  }, [depts, form.mainDept]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    const req = ["staffName", "staffEmail", "staffContact",
                 "mainDept", "subDept",
                 "utility_purpose", "required_date", "required_time"];
    for (const k of req) {
      if (!String(form[k] || "").trim()) {
        toast.error(`Please fill: ${k}`);
        return false;
      }
    }
    if (!(form.is_hod_self === true || form.is_admin1_self === true)
        && !String(form.hodApproval || "").trim()) {
      toast.error("Please select an HOD or declare Yes on Are you a HOD? / Admin-1?");
      return false;
    }
    if (form.is_emergency && (form.emergency_reason || "").trim().length < 10) {
      toast.error("Emergency Reason is mandatory (min 10 characters).");
      return false;
    }
    return true;
  };

  const submit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const form_data = stripSelfSkipTransients(form);
      const payload = { form_type: "maini", parent_ref_code: parentRefCode || undefined, form_data };
      const { data } = await api.post("/requisitions/maini", payload);
      toast.success(`Submitted · ${data.ref_code}`, { duration: 5000 });
      nav(`/requisitions/${data.ref_code}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const hodOptional = isHodDropdownOptional(form);

  return (
    <Layout>
      <Toaster position="top-right" />
      <div className="max-w-4xl mx-auto relative">
        <FormLogo />
        <TopNotice>
          Book the school's Internal Maini utility vehicle for on-campus material
          movement or short-distance transport. CAO approves first; the request
          is then dispatched to the Process Owner (Transport) for fulfilment.
        </TopNotice>

        <div className="mt-4 mb-6">
          <div className="text-[11px] uppercase tracking-widest text-[#2d6b6b] font-semibold">
            Internal Maini Booking
          </div>
          <h1 className="font-serif text-3xl sm:text-4xl font-bold text-[#1f2320] leading-tight"
              data-testid="maini-form-heading">
            <span className="inline-flex items-center gap-2">
              <Truck size={28} className="text-[#2d6b6b]" /> Request an internal Maini
            </span>
          </h1>
        </div>

        <EmergencyToggle
          isEmergency={form.is_emergency}
          onToggle={(v) => setForm((f) => ({
            ...f, is_emergency: v,
            emergency_reason: v ? f.emergency_reason : "",
          }))}
          emergencyReason={form.emergency_reason}
          onReasonChange={(v) => setField("emergency_reason", v)}
        />

        <ResubmitFromSection
          isResubmit={isResubmit}
          onToggle={(v) => { setIsResubmit(v); if (!v) setParentRefCode(null); }}
          parentRefCode={parentRefCode}
          onSelectHistory={(fd, ref) => {
            setForm((f) => ({ ...f, ...fd }));
            setParentRefCode(ref);
          }}
        />

        <ApplicantSpocSection form={form} setField={setField} />

        <RequestedBySection
          form={form} setField={setField}
          depts={depts} hods={hods} subDepts={subDepts}
          hodOptional={hodOptional}
        />

        <SelfSkipSection form={form} setField={setField}
                         userRole={user?.role} />

        <Section numeral="III" title="Requirement details">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <Field label="Utility Purpose" required
                     hint="e.g. Transporting equipment to auditorium">
                <input className="input-vgws"
                       value={form.utility_purpose}
                       onChange={(e) => setField("utility_purpose", e.target.value)}
                       placeholder="e.g. Transporting equipment to auditorium"
                       data-testid="maini-utility-purpose" />
              </Field>
            </div>
            <Field label="Required Date" required>
              <input type="date" className="input-vgws"
                     value={form.required_date}
                     onChange={(e) => setField("required_date", e.target.value)}
                     data-testid="maini-required-date" />
            </Field>
            <Field label="Required Time" required>
              <input type="time" className="input-vgws"
                     value={form.required_time}
                     onChange={(e) => setField("required_time", e.target.value)}
                     data-testid="maini-required-time" />
            </Field>
          </div>
        </Section>

        <div className="mt-6 flex items-center justify-end gap-2">
          <button className="btn-vgws btn-teal"
                  onClick={submit}
                  disabled={submitting}
                  data-testid="maini-submit">
            <Send size={16} /> {submitting ? "Submitting…" : "Submit for approval"}
          </button>
        </div>
      </div>
    </Layout>
  );
}
